import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getTenants, updateTenantStatus, getUsers, getProducts, getMultipleIntegrationsStatus, deleteUser } from '../../services/api';
import { Tenant, TenantLevel, User, TenantType } from '../../types';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal, { ModalFooter } from '../../components/ui/Modal';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash, 
  CheckCircle, 
  XCircle, 
  Users, 
  Download,
  RefreshCw,
  SlidersHorizontal,
  Building,
  Layers,
  X,
  AlertTriangle,
  List,
  Grid,
  Maximize2,
  ExternalLink,
  Package,
  Archive
} from 'lucide-react';
import TenantDetailModal from '../../components/tenants/TenantDetailModal';
import TenantFormModal from '../../components/tenants/TenantFormModal';
import UserFormModal from '../../components/users/UserFormModal';
import UserDetailModal from '../../components/users/UserDetailModal';
import { formatDate, getTenantLevelLabel, getTenantTypeLabel, getUserFullName } from '../../utils/format';
import { exportTenantsToCSV, exportUsersToCSV } from '../../utils/exportUtils';
import EmptyState from '../../components/ui/EmptyState';
import ActionButtons from '../../components/ui/ActionButtons';
import TenantCardContent from '../../components/tenants/TenantListPage';
import { useToast } from '../../components/ui/ToastProvider';
import ConfirmationModal from '../../components/ui/ConfirmationModal';

// Optimize the product count display to prevent flickering
const ProductCountDisplay = React.memo(({ tenantId, isLoadingIntegrations, getCount }: { 
  tenantId: string, 
  isLoadingIntegrations: boolean,
  getCount: (id: string) => number
}) => {
  const count = getCount(tenantId);
  
  return (
    <span className="ml-1 inline-flex items-center">
      <Package size={12} className="mr-0.5 text-green-400 flex-shrink-0" />
      {isLoadingIntegrations ? (
        <span className="text-xs">...</span>
      ) : (
        <span>{count}</span>
      )}
    </span>
  );
}, (prevProps, nextProps) => {
  // Custom equality check to prevent unnecessary re-renders
  return prevProps.tenantId === nextProps.tenantId && 
    prevProps.isLoadingIntegrations === nextProps.isLoadingIntegrations &&
    prevProps.getCount(prevProps.tenantId) === nextProps.getCount(nextProps.tenantId);
});

const TenantListPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { canDeleteUsers, canCreateUsers, canViewUsers, canViewTenants, canEditUsers, canEditTenants, canDeleteTenants, canCreateTenants } = usePermissions();
  const { refreshRBAC, rbacData } = useRBAC();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [filteredTenants, setFilteredTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(() => {
    // Always default to cards view, but still save user preference if they change it
    return 'cards';
  });
  const [productIntegrations, setProductIntegrations] = useState<Record<string, Record<string, boolean>>>({});
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(false);
  
  // Tenant detail modal state
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  
  // Tenant form modal state
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [formModalType, setFormModalType] = useState<'org' | 'partner'>('org');
  const [editTenantId, setEditTenantId] = useState<string | undefined>(undefined);
  
  // User form modal state
  const [isUserFormModalOpen, setIsUserFormModalOpen] = useState(false);
  const [editUserId, setEditUserId] = useState<string | undefined>(undefined);
  
  // User detail modal state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isUserDetailModalOpen, setIsUserDetailModalOpen] = useState(false);
  
  // User delete confirmation modal state
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState<boolean>(false);
  
  // Only save to localStorage when viewMode changes, don't read from it during initialization
  useEffect(() => {
    localStorage.setItem('tenantListViewMode', viewMode);
  }, [viewMode]);
  
  // Refresh RBAC data when component mounts to ensure permissions are up to date
  useEffect(() => {
    refreshRBAC();
  }, [refreshRBAC]);
  

  
  // Handle opening the detail modal
  const handleOpenDetailModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setIsDetailModalOpen(true);
  };
  
  // Handle opening the form modal for creating a new tenant
  const handleOpenFormModal = (type: 'org' | 'partner') => {
    setFormModalType(type);
    setEditTenantId(undefined);
    
    // If opening the form from a URL with a specific level parameter, use that level
    const levelFromUrl = searchParams.get('level');
    if (levelFromUrl === 'L1' || levelFromUrl === 'L2' || levelFromUrl === 'L3' || levelFromUrl === 'L0') {
      // Using level from URL
      // This will be used directly in the TenantFormModal component
    }
    
    setIsFormModalOpen(true);
  };
  
  // Handle opening the form modal for editing a tenant
  const handleEditTenant = (tenantId: string) => {
    setIsDetailModalOpen(false);
    setEditTenantId(tenantId);
    setIsFormModalOpen(true);
  };
  
  // Handle closing the form modal
  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setEditTenantId(undefined);
    fetchTenants(true);
  };
  
  // Handle opening the user form modal for creating a new user
  const handleOpenUserFormModal = () => {
    // Only open if user has permission to create users
    if (!canCreateUsers()) {
      toast.error("You don't have permission to create users");
      return;
    }
    setEditUserId(undefined);
    setIsUserFormModalOpen(true);
  };
  
  // Handle opening the user form modal for editing a user
  const handleEditUser = (userId: string) => {
    // Check if user is editing themselves or has edit permission
    const isEditingSelf = userId === currentUser?.id;
    
    // Allow self-editing without permission check (but role changes will be restricted in the modal)
    // For editing others, require edit permission
    if (!isEditingSelf && !canEditUsers()) {
      toast.error("You don't have permission to edit this user");
      return;
    }
    
    setEditUserId(userId);
    setIsUserFormModalOpen(true);
  };
  
  // Handle closing the user form modal
  const handleCloseUserFormModal = async () => {
    setIsUserFormModalOpen(false);
    // Refresh the users list
    await fetchUsers();
  };
  
  // After the handleCloseUserFormModal function, add the handleOpenUserDetailModal function
  const handleOpenUserDetailModal = (userId: string) => {
    setSelectedUserId(userId);
    setIsUserDetailModalOpen(true);
  };

  // Handle closing the user detail modal
  const handleCloseUserDetailModal = () => {
    setSelectedUserId(null);
    setIsUserDetailModalOpen(false);
  };

  const handleDeleteUser = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) {
      toast.error('User not found');
      return;
    }
    
    setUserToDelete(user);
    setIsDeleteConfirmationOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setIsDeletingUser(true);
      await deleteUser(userToDelete.id);
      toast.success(`User "${getUserFullName(userToDelete)}" deleted successfully`);
      
      // Refresh the users list
      await fetchUsers();
      
      // Close modal and reset state
      setIsDeleteConfirmationOpen(false);
      setUserToDelete(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to delete user';
      toast.error(errorMessage);
    } finally {
      setIsDeletingUser(false);
    }
  };

  const cancelDeleteUser = () => {
    setIsDeleteConfirmationOpen(false);
    setUserToDelete(null);
  };
  
  // Get filter values from URL or defaults
  const selectedLevel = searchParams.get('level') || '';
  const selectedType = searchParams.get('type') || '';
  const statusParam = searchParams.get('status');
  // Missing status in URL defaults to active; explicit '' means All statuses
  const selectedStatus = statusParam === null ? 'active' : statusParam;
  
  // Determine if this is the partners view or organizations view or users view
  const isPartnersView = selectedLevel === 'L0,L2' || selectedType === 'partner';
  const isOrganizationsView = selectedLevel === 'L3' || selectedType === 'org';
  const isUsersView = searchParams.get('view') === 'users';
  const isDeletedView = selectedStatus === 'deleted';
  
  // Handle edit parameter from URL (for settings modal navigation)
  useEffect(() => {
    const editUserId = searchParams.get('edit');
    if (editUserId && isUsersView) {
      // Only open edit modal if we're not already viewing a user detail
      if (!isUserDetailModalOpen) {
        setEditUserId(editUserId);
        setIsUserFormModalOpen(true);
        // Clean up the URL parameter
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('edit');
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [searchParams, isUsersView, isUserDetailModalOpen, setSearchParams]);
  
  // Track view state changes
  useEffect(() => {
    // View state updated
  }, [isUsersView, viewMode, isPartnersView, isOrganizationsView]);
  
  // Set page title based on view
  const pageTitle = isPartnersView 
    ? "Partner Management" 
    : isOrganizationsView 
      ? "Customer Management" 
      : isUsersView
        ? "User Management"
        : "Tenant Management";
  
  // Redirect to Customers view if no filter is selected and set initial filters
  useEffect(() => {
    // Set default filters based on the view
    if (isPartnersView && (!selectedLevel || !selectedType)) {
      navigate('/tenants?level=L0,L1,L2&type=partner&status=active', { replace: true });
      return;
    }
    
    if (isOrganizationsView && (!selectedLevel || !selectedType)) {
      navigate('/tenants?level=L3&type=org&status=active', { replace: true });
      return;
    }
    
    if (!selectedLevel && !selectedType && !isUsersView) {
      navigate('/tenants?level=L3&type=org&status=active', { replace: true });
    }
  }, [selectedLevel, selectedType, navigate, isUsersView, isPartnersView, isOrganizationsView]);

  // Default status filter to active when opening tenant list without a status param
  useEffect(() => {
    if (isUsersView) return;
    if (statusParam === null) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('status', 'active');
      setSearchParams(newParams, { replace: true });
    }
  }, [isUsersView, statusParam, searchParams, setSearchParams]);
  
  // Force re-fetch when returning to this page from other pages
  useEffect(() => {
    // This will execute when the component mounts or re-mounts
    if (!isUsersView) {
      fetchTenants();
    } else {
      fetchUsers();
    }
  }, []);
  
  // Fetch data when filters change via URL parameters
  useEffect(() => {
    if (currentUser) {
      // URL parameters changed, re-fetching data
      
      if (isUsersView) {
        fetchUsers();
      } else {
        fetchTenants();
      }
    }
  }, [currentUser, selectedLevel, selectedType, selectedStatus, isUsersView]);
  
  // Fetch tenants and product integrations
  const fetchTenants = async (showRefresh = false) => {
    if (isUsersView) {
      // If this is the users view, you'd fetch users instead
      setTenants([]);
      setFilteredTenants([]);
      setIsLoading(false);
      return;
    }
    
    if (showRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    
    try {
      const params: any = {};
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      if (selectedLevel) {
        params.level = selectedLevel;
      }
      
      if (selectedType) {
        params.type = selectedType;
      }
      
      // Handle status parameter
      if (selectedStatus === 'deleted') {
        params.is_deleted = true;
      } else if (statusParam === '') {
        // All: non-deleted tenants of any status (active, inactive, pending)
        params.is_deleted = false;
      } else if (['active', 'inactive', 'pending'].includes(selectedStatus)) {
        params.status = selectedStatus;
        params.is_deleted = false;
      } else {
        params.status = 'active';
        params.is_deleted = false;
      }
      
      // If no specific params are set, apply defaults based on user tenant level
      if (!selectedLevel && !selectedType) {
        if (currentUser?.tenant_level === 'L0') {
          // All tenants
        } else if (currentUser?.tenant_level === 'L1' || currentUser?.tenant_level === 'L2') {
          params.parent_id = currentUser.tenant_id || '';
        } else if (currentUser?.tenant_id) {
          params.id = currentUser.tenant_id;
        }
      }
      
      const tenantsData = await getTenants(params);
      
      setTenants(tenantsData);
      setFilteredTenants(tenantsData);

      // If we have tenants, fetch their product integrations
      if (tenantsData.length > 0) {
        fetchTenantIntegrations(tenantsData);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };
  
  // Optimized fetchTenantIntegrations function to reduce flickering
  const fetchTenantIntegrations = async (tenantList: Tenant[]) => {
    if (tenantList.length === 0) return;
    
    // Only set loading state once at the beginning
    setIsLoadingIntegrations(true);
      
    try {
      // Get all tenant IDs
        const tenantIds = tenantList.map(tenant => tenant.id);
        
      // Get all product IDs (only once)
      const productsData = await getProducts();
      const productIds = productsData.map(p => p.id || p._id as string).filter(Boolean);
      
      if (productIds.length > 0 && tenantIds.length > 0) {
        // Fetch all integrations in a single API call
        const integrationsData = await getMultipleIntegrationsStatus(tenantIds, productIds);
        
        // Process the data without triggering multiple state updates
        const newIntegrations: Record<string, Record<string, boolean>> = {};
        
        // Initialize the structure for all tenants
        tenantIds.forEach(tenantId => {
          newIntegrations[tenantId] = {};
          
          // Initialize all products for this tenant
          productIds.forEach(productId => {
            newIntegrations[tenantId][productId] = false;
          });
        });
        
        // Update with actual integration data
        if (integrationsData && typeof integrationsData === 'object') {
          Object.keys(integrationsData).forEach(productId => {
            const productIntegrations = integrationsData[productId];
            
            if (productIntegrations) {
              Object.keys(productIntegrations).forEach(tenantId => {
                const integration = productIntegrations[tenantId];
                
                if (integration && newIntegrations[tenantId]) {
                  // Check for active status in the active_status field (new API format)
                  // or in the integration object itself (old format)
                  const isActive = 
                    integrationsData.active_status?.[productId]?.[tenantId] !== undefined
                      ? integrationsData.active_status[productId][tenantId]
                      : (typeof integration === 'boolean' 
                          ? integration 
                          : integration.active || false);
                  
                  newIntegrations[tenantId][productId] = isActive;
                }
              });
            }
          });
        }
        
        // Use functional update to ensure we're not depending on previous state
        setProductIntegrations(newIntegrations);
      }
    } catch (error) {
    } finally {
      setIsLoadingIntegrations(false);
    }
  };
  
  // Filter tenants based on search term and other filters
  useEffect(() => {
    if (isUsersView) return;
    
    let result = [...tenants];
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(tenant => 
        tenant.name.toLowerCase().includes(term) || 
        tenant.tenant_code.toLowerCase().includes(term)
      );
    }
    
    // Sort tenants alphabetically by name
    result.sort((a, b) => a.name.localeCompare(b.name));
    
    // Use functional update to avoid dependency on previous state
    setFilteredTenants(result);
    
    // Only include the dependencies that should trigger a re-filter
  }, [searchTerm, tenants, isUsersView]);
  
  // Fetch users for the Users tab
  const fetchUsers = async (showRefresh = false) => {
    if (!isUsersView) return;
    
    if (showRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoadingUsers(true);
    }
    
    try {
      // Fetch users based on user's tenant level
      let params: Record<string, string> = {};
      
        if (currentUser?.tenant_level === 'L3') {
        // Only fetch users for the current tenant
          params = { tenant_id: currentUser.tenant_id || '' };
        } else if (currentUser?.tenant_level === 'L0' && currentUser?.role?.toLowerCase() === 'admin') {
        // L0 admins should see all users from all tenant levels
        // Don't pass any tenant_id parameter
        params = {};
      }
      // For L1, L2 users, let the backend handle the hierarchy-based filtering
      
      const usersData = await getUsers(params);
      setUsers(usersData);
      setFilteredUsers(usersData);
    } catch (error) {
    } finally {
      setIsLoadingUsers(false);
      setIsRefreshing(false);
    }
  };
  
  // Filter users based on search term
  useEffect(() => {
    if (!isUsersView) return;
    
    let result = [...users];
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(user => 
        user.username.toLowerCase().includes(term) || 
        user.email.toLowerCase().includes(term) ||
        (user.first_name && user.first_name.toLowerCase().includes(term)) ||
        (user.last_name && user.last_name.toLowerCase().includes(term))
      );
    }
    
    // Sort users alphabetically by username or name
    result.sort((a, b) => {
      // Sort by full name if available, otherwise by username
      const nameA = a.first_name && a.last_name ? `${a.first_name} ${a.last_name}` : a.username;
      const nameB = b.first_name && b.last_name ? `${b.first_name} ${b.last_name}` : b.username;
      return nameA.localeCompare(nameB);
    });
    
    setFilteredUsers(result);
  }, [searchTerm, users, isUsersView]);
  
  // Update filters in URL
  const updateFilters = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };
  
  // Handle status change
  const handleStatusChange = (tenant: Tenant, status: string) => {
    if (tenant.is_deleted) {
      toast.error('Cannot change status of a deleted tenant. Deleted tenants cannot be reactivated.');
      return;
    }
    setTenantToChangeStatus(tenant);
    setNewStatus(status);
    setIsStatusModalOpen(true);
  };

  const getTenantActionButtonProps = (tenant: Tenant) => {
    if (tenant.is_deleted || isDeletedView) {
      return {
        onViewDetails: () => handleOpenDetailModal(tenant),
        showStatus: false,
        isActive: false,
      };
    }
    return {
      onEdit: canEditTenants() ? () => handleEditTenant(tenant.id) : undefined,
      onDelete: canDeleteTenants() ? () => handleDeleteClick(tenant) : undefined,
      onActivate:
        tenant.status !== 'active' && canEditTenants()
          ? () => handleStatusChange(tenant, 'active')
          : undefined,
      onDeactivate:
        tenant.status !== 'inactive' && canEditTenants()
          ? () => handleStatusChange(tenant, 'inactive')
          : undefined,
      onViewDetails: () => handleOpenDetailModal(tenant),
      isActive: tenant.status === 'active',
    };
  };
  
  // Handle delete tenant
  const handleDeleteClick = (tenant: Tenant) => {
    setTenantToDelete(tenant);
  };
  
  // Confirm delete tenant
  const confirmDelete = async () => {
    if (!tenantToDelete) return;
    
    setIsDeleting(true);
    
    try {
      // Use the special 'deleted' status to set is_deleted=true and status to inactive
      await updateTenantStatus(tenantToDelete.id, 'deleted');
      
      // Remove the tenant from the current list
      const updatedTenants = tenants.filter(tenant => tenant.id !== tenantToDelete.id);
      
      setTenants(updatedTenants);
      setFilteredTenants(updatedTenants);
      setTenantToDelete(null);
      
      // Show success message
      toast.success(`Tenant ${tenantToDelete.name} has been moved to Deleted and set to inactive.`);
    } catch (error: any) {
      // Show error message
      toast.error(error.response?.data?.error || "Failed to delete tenant. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Modal state
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [tenantToChangeStatus, setTenantToChangeStatus] = useState<Tenant | null>(null);
  const [newStatus, setNewStatus] = useState<string>('');
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  
  // Update tenant status
  const confirmStatusChange = async () => {
    if (!tenantToChangeStatus || !newStatus) return;
    
    setIsChangingStatus(true);
    
    try {
      const tenantName = tenantToChangeStatus.name;
      await updateTenantStatus(tenantToChangeStatus.id, newStatus);

      setIsStatusModalOpen(false);
      setTenantToChangeStatus(null);
      await fetchTenants(true);

      toast.success(`Tenant ${tenantName} status changed to ${newStatus}.`);
    } catch (error: any) {
      // Show error toast or notification
      toast.error(error.response?.data?.error || "Failed to update tenant status. Please try again.");
    } finally {
      setIsChangingStatus(false);
    }
  };
  
  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    if (isPartnersView) {
      setSearchParams({ level: 'L0,L2', type: 'partner', status: 'active' });
    } else if (isOrganizationsView) {
      setSearchParams({ level: 'L3', type: 'org', status: 'active' });
    } else if (isUsersView) {
      setSearchParams({ view: 'users' });
    } else {
      setSearchParams({});
    }
    setIsAdvancedFiltersOpen(false);
  };
  
  // Handle refresh
  const handleRefresh = () => {
    if (isUsersView) {
      fetchUsers(true);
    } else {
      fetchTenants(true);
    }
  };
  
  // Export to CSV
  const handleExportCSV = () => {
    if (isUsersView) {
      exportUsersToCSV(filteredUsers);
    } else {
      exportTenantsToCSV(filteredTenants);
    }
  };
  
  // Memoize filtered tenants to prevent unnecessary re-renders
  const memoizedFilteredTenants = useMemo(() => {
    return filteredTenants;
  }, [filteredTenants]);
  
  // Memoize the tenant count function to prevent recreating it on each render
  const getTenantIntegratedProductCount = useCallback((tenantId: string) => {
    if (!productIntegrations || Object.keys(productIntegrations).length === 0) {
      return 0;
    }

    let count = 0;
    const tenantIntegrations = productIntegrations[tenantId];
    if (tenantIntegrations) {
      Object.values(tenantIntegrations).forEach(isActive => {
        if (isActive) count++;
    });
    }
    return count;
  }, [productIntegrations]);
  
  // Render tabs for navigation between different views
  const renderTabs = () => {
    return (
      <div className="flex border border-indigo-800/30 rounded-lg overflow-hidden relative z-10 bg-gray-900/60">
        {canViewTenants() && (
        <Link 
          to="/tenants?level=L0,L1,L2&type=partner"
          className={`px-3 py-2 flex items-center relative z-10 cursor-pointer ${isPartnersView && !isDeletedView ? 'bg-blue-600/80 text-white' : 'hover:bg-indigo-900/40 text-gray-300 hover:text-blue-300'}`}
        >
          <Building size={18} className="mr-2" />
          <span className="relative z-10">Partners</span>
        </Link>
        )}
        
        {canViewTenants() && (
        <Link 
          to="/tenants?level=L3&type=org"
          className={`px-3 py-2 flex items-center relative z-10 cursor-pointer ${isOrganizationsView && !isDeletedView ? 'bg-blue-600/80 text-white' : 'hover:bg-indigo-900/40 text-gray-300 hover:text-blue-300'}`}
        >
          <Layers size={18} className="mr-2" />
          <span className="relative z-10">Customers</span>
        </Link>
        )}
        
        {canViewUsers() && (
        <Link 
          to="/tenants?view=users"
          className={`px-3 py-2 flex items-center relative z-10 cursor-pointer ${isUsersView ? 'bg-blue-600/80 text-white' : 'hover:bg-indigo-900/40 text-gray-300 hover:text-blue-300'}`}
        >
          <Users size={18} className="mr-2" />
          <span className="relative z-10">Users</span>
        </Link>
        )}
        
        <Link 
          to="/tenants?status=deleted"
          className={`px-3 py-2 flex items-center relative z-10 cursor-pointer ${isDeletedView ? 'bg-purple-600/80 text-white' : 'hover:bg-indigo-900/40 text-gray-300 hover:text-blue-300'}`}
        >
          <Archive size={18} className="mr-2" />
          <span className="relative z-10">Deleted</span>
        </Link>
      </div>
    );
  };
  
  // Render the users view
  const renderUsersView = () => {
    // Rendering users view
    
    if (isLoadingUsers) {
      return (
        <div className="card flex justify-center items-center py-16">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-text-muted-dark">Loading users...</p>
          </div>
        </div>
      );
    }
    
    if (filteredUsers.length === 0) {
      return (
        <div className="card">
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Users size={48} className="text-text-muted-dark mb-4" />
            <h3 className="text-xl font-medium mb-2">No Users Found</h3>
            <p className="text-text-muted-dark text-center max-w-md mb-6">
              {searchTerm ? "Try adjusting your search terms" : "Get started by adding your first user"}
            </p>
            <button 
              onClick={handleOpenUserFormModal}
              className="btn-primary flex items-center"
            >
              <Plus size={16} className="mr-2" />
              Add User
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <>
        {/* Mobile card view (always use cards on mobile) */}
        <div className="block md:hidden">
          <div className="space-y-4">
            {filteredUsers.map((user) => (
              <div 
                key={user.id}
                className="card hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => handleOpenUserDetailModal(user.id)}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center mr-3">
                      <span className="font-medium text-xs">
                        {user.first_name && user.last_name 
                          ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}` 
                          : user.email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-medium">{user.username}</h3>
                      <p className="text-xs text-text-muted-dark mt-1">{getUserFullName(user)}</p>
                    </div>
                  </div>
                  <Badge 
                    variant={user.status === 'active' ? 'success' : 'danger'}
                    withDot={user.status === 'active'}
                    size="sm"
                  >
                    {user.status}
                  </Badge>
                </div>
                
                <div className="border-t border-border-dark pt-3 flex justify-end" onClick={(e) => e.stopPropagation()}>
                  <ActionButtons
                    onEdit={canEditUsers() ? () => handleEditUser(user.id) : undefined}
                    onDelete={canDeleteUsers() ? () => handleDeleteUser(user.id) : undefined}
                    onViewDetails={() => handleOpenUserDetailModal(user.id)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Desktop view - Card or Table based on viewMode */}
        <div className="hidden md:block">
          {/* Desktop view rendering */}
          {viewMode === 'cards' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredUsers.map((user) => (
                <div 
                  key={user.id}
                  className="card hover:border-primary/50 transition-colors relative z-10 cursor-pointer"
                  onClick={() => handleOpenUserDetailModal(user.id)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center mr-3">
                        <span className="font-medium text-xs">
                          {user.first_name && user.last_name 
                            ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}` 
                            : user.email.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-medium">{user.username}</h3>
                        <p className="text-xs text-text-muted-dark mt-1">{getUserFullName(user)}</p>
                      </div>
                    </div>
                    <Badge 
                      variant={user.status === 'active' ? 'success' : 'danger'}
                      withDot={user.status === 'active'}
                    >
                      {user.status}
                    </Badge>
                  </div>
                  
                  <div className="border-t border-border-dark pt-3 mb-4">
                    <div className="grid grid-cols-1 gap-y-2">
                      <div>
                        <span className="text-xs text-text-muted-dark block">Email</span>
                        <span className="text-sm">{user.email}</span>
                      </div>
                      <div>
                        <span className="text-xs text-text-muted-dark block">Role</span>
                        <span className="text-sm">{user.role}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-border-dark pt-3 flex justify-between items-center" onClick={(e) => e.stopPropagation()}>
                    <span className="text-xs text-text-muted-dark">
                      {user.last_login ? formatDate(user.last_login) : 'Never logged in'}
                    </span>
                    <ActionButtons
                      onEdit={canEditUsers() ? () => handleEditUser(user.id) : undefined}
                      onDelete={canDeleteUsers() ? () => handleDeleteUser(user.id) : undefined}
                      onViewDetails={() => handleOpenUserDetailModal(user.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card overflow-hidden">
              {/* Table view for users */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border-dark">
                      <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Username</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Name</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Email</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Role</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Status</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Last Login</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr 
                        key={user.id} 
                        className="border-b border-border-dark/40 hover:bg-background-hover/30 transition-colors cursor-pointer"
                        onClick={() => handleOpenUserDetailModal(user.id)}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center mr-3">
                              <span className="font-medium text-xs">
                                {user.first_name && user.last_name 
                                  ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}` 
                                  : user.email.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="font-medium">{user.username}</div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {getUserFullName(user)}
                        </td>
                        <td className="py-3 px-4 text-sm">{user.email}</td>
                        <td className="py-3 px-4">
                          <Badge 
                            variant={
                              user.role?.toLowerCase() === 'admin' ? 'info' : 
                              user.role === 'manager' ? 'warning' : 
                              'default'
                            }
                            size="sm"
                          >
                            {user.role}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge 
                            variant={user.status === 'active' ? 'success' : 'danger'}
                            withDot={user.status === 'active'}
                            size="sm"
                          >
                            {user.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm">{user.last_login ? formatDate(user.last_login) : 'Never logged in'}</td>
                        <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <ActionButtons
                            onEdit={canEditUsers() ? () => handleEditUser(user.id) : undefined}
                            onDelete={canDeleteUsers() ? () => handleDeleteUser(user.id) : undefined}
                            onViewDetails={() => handleOpenUserDetailModal(user.id)}
                            size="sm"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-4 text-sm text-text-muted-dark">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </>
    );
  };
  
  // Enhance the viewMode toggle buttons with explicit handler functions
  const handleViewModeChange = (mode: 'cards' | 'table') => {
            // Setting viewMode
    setViewMode(mode);
  };
  
  // Check if user has any permissions to view this page
  const hasAnyPermission = canViewTenants() || canViewUsers();
  
  // If user has no permissions, show access denied
  if (!hasAnyPermission) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Access Denied</h2>
          <p className="text-gray-400">
            You don't have permission to view this page.
          </p>
        </div>
      </div>
    );
  }
  
  // Main render method
    return (
    <div className="p-6">
      {/* Tenant Detail Modal */}
      <TenantDetailModal
        tenant={selectedTenant}
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
      />
      
      {/* Tenant Form Modal */}
      <TenantFormModal
        isOpen={isFormModalOpen}
        onClose={handleCloseFormModal}
        onSaved={handleCloseFormModal}
        tenantId={editTenantId}
        initialLevel={
          // If there's a specific level in the URL, prioritize it
          searchParams.get('level') === 'L1' ? TenantLevel.L1 :
          searchParams.get('level') === 'L2' ? TenantLevel.L2 :
          searchParams.get('level') === 'L3' ? TenantLevel.L3 :
          searchParams.get('level') === 'L0' ? TenantLevel.L0 :
          // Otherwise fall back to the formModalType default
          formModalType === 'partner' ? TenantLevel.L2 : TenantLevel.L3
        }
        initialType={formModalType === 'partner' ? TenantType.PARTNER : TenantType.ORG}
      />
      
      {/* User Form Modal - only render when in users view and modal should be open */}
      {isUsersView && isUserFormModalOpen && (
      <UserFormModal
        userId={editUserId}
        isOpen={isUserFormModalOpen}
        onClose={handleCloseUserFormModal}
        onSaved={fetchUsers}
      />
      )}
      
      {/* User Detail Modal - only render when in users view */}
      {isUsersView && selectedUserId && (
        <UserDetailModal
          userId={selectedUserId}
          isOpen={isUserDetailModalOpen}
          onClose={handleCloseUserDetailModal}
        />
      )}
      
      {/* User Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteConfirmationOpen}
        onClose={cancelDeleteUser}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message={
          userToDelete 
            ? `Are you sure you want to delete user "${getUserFullName(userToDelete)}" (${userToDelete.email})?\n\nThis action cannot be undone and will permanently remove the user from the system.`
            : ''
        }
        confirmText="Delete User"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeletingUser}
      />
      
      {/* Header with title and action buttons */}
      <div className="relative z-10 mb-6">
        <div className="bg-gradient-to-b from-gray-900/90 via-indigo-900/20 to-transparent p-6 rounded-xl backdrop-blur-sm border border-indigo-800/20 shadow-lg">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-300">{pageTitle}</h1>
              <p className="text-gray-400 mt-1">
                {isPartnersView 
                  ? "Manage your partner tenants and their information"
                  : isOrganizationsView
                    ? "Manage your customers tenants and their information"
                    : isUsersView
                      ? "Manage users and their access permissions"
                      : "Manage all tenants in the system"}
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2">
                {canViewTenants() && (
                <Link 
                  to="/tenants?level=L0,L1,L2&type=partner"
                  className={`px-4 py-2 rounded-lg flex items-center ${
                    isPartnersView 
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <Building size={16} className="mr-2" />
                  Partners
                </Link>
                )}
                
                {canViewTenants() && (
                <Link 
                  to="/tenants?level=L3&type=org"
                  className={`px-4 py-2 rounded-lg flex items-center ${
                    isOrganizationsView 
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <Layers size={16} className="mr-2" />
                  Customers
                </Link>
                )}
                
                {canViewUsers() && (
                <Link 
                  to="/tenants?view=users"
                  className={`px-4 py-2 rounded-lg flex items-center ${
                    isUsersView
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <Users size={16} className="mr-2" />
                  Users
                </Link>
                )}
              </div>
              
                {!isUsersView && (
                 canCreateTenants() && (
                   <button 
                     onClick={() => handleOpenFormModal(isOrganizationsView ? 'org' : 'partner')}
                     className="p-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-md shadow-lg flex items-center justify-center transition-all duration-200 hover:shadow-xl"
                     title={`Add ${isOrganizationsView ? "Organization" : "Partner"}`}
                   >
                     <Plus size={18} />
                   </button>
                 )
                )}
              
              {isUsersView && canCreateUsers() && (
                <button 
                  onClick={handleOpenUserFormModal}
                  className="relative z-10 p-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-md shadow-lg flex items-center justify-center transition-all duration-200 hover:shadow-xl"
                  title="Add User"
                >
                  <Plus size={18} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Modern filter controls */}
      <div className="mb-6 bg-background-card border border-card-border rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Bar */}
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={18} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={isUsersView ? "Search users..." : "Search tenants..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-background-hover border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <X size={16} className="text-gray-400 hover:text-white transition-colors" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex items-center gap-2">
              <button
                className={`p-2 rounded-md border ${
                  viewMode === 'cards'
                    ? 'bg-primary/20 border-primary/40 text-primary-300'
                    : 'border-border-dark bg-background-hover text-gray-400 hover:text-gray-200'
                }`}
                onClick={() => handleViewModeChange('cards')}
                title="Card View"
                aria-label="Switch to Card View"
              >
                <Grid size={18} />
              </button>
              <button
                className={`p-2 rounded-md border ${
                  viewMode === 'table'
                    ? 'bg-primary/20 border-primary/40 text-primary-300'
                    : 'border-border-dark bg-background-hover text-gray-400 hover:text-gray-200'
                }`}
                onClick={() => handleViewModeChange('table')}
                title="Table View"
                aria-label="Switch to Table View"
              >
                <List size={18} />
              </button>
            </div>
            
            <button
              onClick={handleRefresh}
              className="p-2 rounded-md border border-border-dark bg-background-hover text-gray-400 hover:text-gray-200"
              disabled={isRefreshing}
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
            </button>
            
            <button
              onClick={handleExportCSV}
              className="p-2 rounded-md border border-border-dark bg-background-hover text-gray-400 hover:text-gray-200"
              aria-label="Export"
              title="Export to CSV"
            >
              <Download size={18} />
            </button>
            
            {/* Filter Toggle Button */}
            <button
              onClick={() => setIsAdvancedFiltersOpen(!isAdvancedFiltersOpen)}
              className={`flex items-center px-4 py-2 rounded-lg border transition-all ${
                isAdvancedFiltersOpen 
                  ? 'bg-primary/20 border-primary/40 text-primary-300' 
                  : 'border-border-dark bg-background-hover text-gray-300 hover:bg-background-darker'
              }`}
            >
              <SlidersHorizontal size={16} className="mr-2" />
              Filters
              {(selectedStatus) && (
                <span className="ml-2 bg-primary/30 text-primary-300 text-xs py-0.5 px-2 rounded-full">
                  {[selectedStatus].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Expanded Filter Options */}
        {isAdvancedFiltersOpen && (
          <div className="mt-4 pt-4 border-t border-border-dark animate-fadeIn">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {/* Only show tenant filters if not in users view */}
              {!isUsersView && (
                <>
                  <div className="flex items-center text-gray-400 mr-2">
                    <Filter size={16} className="mr-1" />
                    <span className="text-sm">Status:</span>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <button
                      className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                        statusParam === ''
                          ? 'bg-primary text-white shadow-md shadow-primary/20' 
                          : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                      }`}
                      onClick={() => updateFilters('status', '')}
                    >
                      All
                    </button>
                    <button
                      className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                        selectedStatus === 'active' 
                          ? 'bg-green-600 text-white shadow-md shadow-green-500/20' 
                          : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                      }`}
                      onClick={() => updateFilters('status', 'active')}
                    >
                      Active
                    </button>
                    <button
                      className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                        selectedStatus === 'inactive'
                          ? 'bg-red-600 text-white shadow-md shadow-red-500/20' 
                          : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                      }`}
                      onClick={() => updateFilters('status', 'inactive')}
                    >
                      Inactive
                    </button>
                    <button
                      className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                        selectedStatus === 'pending' 
                          ? 'bg-yellow-600 text-white shadow-md shadow-yellow-500/20' 
                          : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                      }`}
                      onClick={() => updateFilters('status', 'pending')}
                    >
                      Pending
                    </button>
                    <button
                      className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                        selectedStatus === 'deleted' 
                          ? 'bg-purple-600 text-white shadow-md shadow-purple-500/20' 
                          : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                      }`}
                      onClick={() => updateFilters('status', 'deleted')}
                    >
                      <Archive size={14} className="mr-1" />
                      Deleted
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Main content area */}
      {isUsersView ? (
        renderUsersView()
      ) : (
        <>
          {isLoading ? (
            <div className="card flex justify-center items-center py-16">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                <p className="text-text-muted-dark">Loading {isOrganizationsView ? 'customers' : 'partners'}...</p>
                  </div>
            </div>
          ) : isLoadingIntegrations ? (
            // Show a skeleton loader while integrations are loading to prevent flickering
            <div className="hidden md:block">
              {viewMode === 'cards' ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {Array.from({ length: Math.min(6, memoizedFilteredTenants.length || 3) }).map((_, index) => (
                    <div key={`skeleton-${index}`} className="card p-4 flex flex-col min-h-[220px] animate-pulse">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center">
                          <div className="w-6 h-6 bg-gray-700 rounded mr-2"></div>
                          <div>
                            <div className="h-4 bg-gray-700 rounded w-32 mb-2"></div>
                            <div className="h-3 bg-gray-800 rounded w-24"></div>
                          </div>
                        </div>
                        <div className="h-5 bg-gray-700 rounded w-16"></div>
                      </div>
                      
                      <div className="border-t border-border-dark pt-2 mb-2 flex-grow">
                        <div className="grid grid-cols-2 gap-y-1 gap-x-2">
                          <div className="h-3 bg-gray-800 rounded w-20 mt-2"></div>
                          <div className="h-3 bg-gray-800 rounded w-20 mt-2"></div>
                          <div className="h-3 bg-gray-800 rounded w-20 mt-2"></div>
                          <div className="h-3 bg-gray-800 rounded w-20 mt-2"></div>
                        </div>
                      </div>
                      
                      <div className="border-t border-border-dark pt-2 flex justify-between items-center">
                        <div className="h-3 bg-gray-800 rounded w-24"></div>
                        <div className="flex space-x-1">
                          <div className="w-6 h-6 bg-gray-700 rounded"></div>
                          <div className="w-6 h-6 bg-gray-700 rounded"></div>
                          <div className="w-6 h-6 bg-gray-700 rounded"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-border-dark">
                          <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Name</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Code</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Level</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Status</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Created</th>
                          <th className="py-3 px-4 text-right text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: Math.min(6, memoizedFilteredTenants.length || 3) }).map((_, index) => (
                          <tr key={`skeleton-row-${index}`} className="border-b border-border-dark/40 animate-pulse">
                            <td className="py-3 px-4"><div className="h-4 bg-gray-700 rounded w-32"></div></td>
                            <td className="py-3 px-4"><div className="h-4 bg-gray-700 rounded w-20"></div></td>
                            <td className="py-3 px-4"><div className="h-4 bg-gray-700 rounded w-12"></div></td>
                            <td className="py-3 px-4"><div className="h-4 bg-gray-700 rounded w-16"></div></td>
                            <td className="py-3 px-4"><div className="h-4 bg-gray-700 rounded w-24"></div></td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex justify-end space-x-1">
                                <div className="w-6 h-6 bg-gray-700 rounded"></div>
                                <div className="w-6 h-6 bg-gray-700 rounded"></div>
                                <div className="w-6 h-6 bg-gray-700 rounded"></div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : filteredTenants.length === 0 ? (
            <div className="card">
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <AlertTriangle size={48} className="text-text-muted-dark mb-4" />
                <h3 className="text-xl font-medium mb-2">No {isOrganizationsView ? 'Customers' : 'Partners'} Found</h3>
                <p className="text-text-muted-dark text-center max-w-md mb-6">
                  {(searchTerm || selectedStatus) 
                    ? "Try adjusting your filters" 
                    : `Get started by adding your first ${isOrganizationsView ? 'customer' : 'partner'}`}
                </p>
                {(searchTerm || selectedLevel || selectedStatus) ? (
                    <button 
              onClick={resetFilters}
                    className="btn-secondary flex items-center"
                    >
                    <Filter size={16} className="mr-2" />
                    Clear Filters
                    </button>
                ) : (
                <button 
                    onClick={() => handleOpenFormModal(isOrganizationsView ? 'org' : 'partner')}
                    className="py-2 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-md shadow-lg flex items-center transition-all duration-200 hover:shadow-xl"
                    title={`Add ${isOrganizationsView ? "Organization" : "Partner"}`}
                >
                    <Plus size={18} className="mr-2" />
                    <span className="font-medium">Add {isOrganizationsView ? "Customer" : "Partner"}</span>
                </button>
            )}
          </div>
      </div>
          ) : (
            <>
              {/* Mobile view - Always card-like */}
              <div className="md:hidden space-y-4">
                {memoizedFilteredTenants.map((tenant) => (
                    <div 
                    key={`tenant-mobile-${tenant.id}`}
                    className="card hover:border-primary/50 transition-colors relative z-10 cursor-pointer flex flex-col min-h-[180px]"
                      onClick={() => handleOpenDetailModal(tenant)}
                    >
                      <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0 mr-2">
                        <TenantCardContent tenant={tenant} />
                      </div>
                        <Badge 
                          variant={
                          tenant.status === 'active' ? 'success' : 
                          tenant.status === 'inactive' ? 'danger' : 
                          'warning'
                          }
                          withDot={tenant.status === 'active'}
                          size="sm"
                        className="flex-shrink-0"
                        >
                          {tenant.status}
                        </Badge>
                      </div>
                      
                    <div className="border-t border-border-dark pt-2 mb-2 flex-grow">
                        <div className="grid grid-cols-2 gap-y-1 gap-x-2 text-xs">
                          <div>
                            <span className="text-text-muted-dark">Level:</span>
                            <span className="ml-1">{getTenantLevelLabel(tenant.level)}</span>
        </div>
                          <div>
                            <span className="text-text-muted-dark">Created:</span>
                            <span className="ml-1">{formatDate(tenant.created_at)}</span>
      </div>
        </div>
      </div>
          
                    <div className="border-t border-border-dark pt-2 flex justify-between items-center mt-auto" onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs text-text-muted-dark">{formatDate(tenant.created_at, true)}</span>
                    <ActionButtons
                      {...getTenantActionButtonProps(tenant)}
                          size="xs"
                    />
                      </div>
                    </div>
              ))}
        </div>
        
              {/* Desktop views - Card or Table based on user preference */}
              <div className="hidden md:block">
                {viewMode === 'cards' ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {memoizedFilteredTenants.map((tenant) => (
                      <div 
                        key={`tenant-card-${tenant.id}`}
                        className="card hover:border-primary/50 transition-colors relative z-10 cursor-pointer p-4 flex flex-col min-h-[220px]"
                        onClick={() => handleOpenDetailModal(tenant)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1 min-w-0 mr-2">
                          <TenantCardContent tenant={tenant} />
                          </div>
                          <Badge 
                            variant={
                              tenant.status === 'active' ? 'success' : 
                              tenant.status === 'inactive' ? 'danger' : 
                              'warning'
                            }
                            withDot={tenant.status === 'active'}
                            size="sm"
                            className="flex-shrink-0"
                          >
                            {tenant.status}
                          </Badge>
                        </div>
                        
                        <div className="border-t border-border-dark pt-2 mb-2 flex-grow">
                          <div className="grid grid-cols-2 gap-y-1 gap-x-2 text-xs">
                            <div>
                              <span className="text-text-muted-dark">Level:</span>
                              <span className="ml-1">{getTenantLevelLabel(tenant.level)}</span>
                            </div>

                            <div>
                              <span className="text-text-muted-dark">Created:</span>
                              <span className="ml-1">{formatDate(tenant.created_at)}</span>
                            </div>

                            <div className="col-span-2">
                              <span className="text-text-muted-dark">Products:</span>
                              <ProductCountDisplay tenantId={tenant.id} isLoadingIntegrations={isLoadingIntegrations} getCount={getTenantIntegratedProductCount} />
                            </div>
                          </div>
                        </div>
                        
                        <div className="border-t border-border-dark pt-2 flex justify-end items-center mt-auto" onClick={(e) => e.stopPropagation()}>
                          <ActionButtons
                            {...getTenantActionButtonProps(tenant)}
                            size="sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="card overflow-hidden">
        <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
            <thead>
                          <tr className="border-b border-border-dark">
                            <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Tenant</th>
                            <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Code</th>
                            <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Level</th>
                            <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Status</th>
                            <th className="py-3 px-4 text-left text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Created</th>
                            <th className="py-3 px-4 text-center text-xs font-semibold text-text-muted-dark uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
                {memoizedFilteredTenants.map((tenant) => (
                            <tr 
                              key={`tenant-table-${tenant.id}`}
                              className="border-b border-border-dark/40 hover:bg-background-hover/30 transition-colors cursor-pointer"
                              onClick={() => handleOpenDetailModal(tenant)}
                            >
                              <td className="py-3 px-4">
                                <div className="flex items-center">
                                  <TenantCardContent tenant={tenant} />
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <code className="text-xs bg-background-darker px-1.5 py-0.5 rounded max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap inline-block">{tenant.tenant_code}</code>
                              </td>
                              <td className="py-3 px-4 text-sm">{getTenantLevelLabel(tenant.level)}</td>
                              <td className="py-3 px-4">
                                <Badge 
                                  variant={
                          tenant.status === 'active' ? 'success' : 
                          tenant.status === 'inactive' ? 'danger' : 
                          'warning'
                                  }
                                  withDot={tenant.status === 'active'}
                                  size="sm"
                                >
                                  {tenant.status}
                                </Badge>
                  </td>
                              <td className="py-3 px-4 text-sm">{formatDate(tenant.created_at)}</td>
                              <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                                <ActionButtons
                                  {...getTenantActionButtonProps(tenant)}
                                  size="sm"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-4 text-sm text-text-muted-dark">
                Showing {filteredTenants.length} of {tenants.length} {isOrganizationsView ? 'customers' : 'partners'}
          </div>
            </>
          )}
        </>
      )}
      
      {/* Status change modal */}
      {isStatusModalOpen && tenantToChangeStatus && (
      <Modal
          title={`${newStatus === 'active' ? 'Activate' : 'Deactivate'} Tenant`}
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        footer={
          <ModalFooter
            onCancel={() => setIsStatusModalOpen(false)}
            onConfirm={confirmStatusChange}
              cancelText="Cancel"
              confirmText={newStatus === 'active' ? 'Activate' : 'Deactivate'}
              isLoading={isChangingStatus}
              confirmVariant={newStatus === 'active' ? 'success' : 'danger'}
            />
          }
        >
          <p>
            Are you sure you want to {newStatus === 'active' ? 'activate' : 'deactivate'} tenant "{tenantToChangeStatus.name}"?
          </p>
      </Modal>
      )}
      
      {/* Delete confirmation modal */}
      {tenantToDelete && (
      <Modal
        title="Delete Tenant"
          isOpen={!!tenantToDelete}
          onClose={() => setTenantToDelete(null)}
        footer={
          <ModalFooter
              onCancel={() => setTenantToDelete(null)}
            onConfirm={confirmDelete}
              cancelText="Cancel"
            confirmText="Delete"
            isLoading={isDeleting}
            confirmVariant="danger"
          />
        }
      >
          <p>
            Are you sure you want to delete tenant "{tenantToDelete.name}"?
          </p>
          <p className="mt-2 text-text-muted-dark text-sm">
            Deleted tenants will be deactivated and moved to the Deleted tab. They cannot be reactivated.
          </p>
      </Modal>
      )}
    </div>
  );
};

export default TenantListPage; 